import {
  closeModal as closeModalAction,
  closeSettings,
  modalStateSelector,
  openModal,
  selectIsMultiselectView,
  selectSelectedContact,
  selectSelectedContactBatch,
  startNewContact,
  startNewNote,
  startNewReminder,
} from '@actions/ui';
import { metaTrigger } from '@support/keyboardHandler';
import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { CommandBarModal } from './CommandBar';
import ReconnectModal from '@components/Modals/Reconnect';
import AddToGroupModal from '@components/Modals/AddToGroup';
import MergeModal from '@components/Modals/Merge';
import ContactAndNoteModal from './NoteModal';
import { archiveContact, toggleStarredStatus } from './CommandBar/actionsBySection/contactActions';
import {
  archiveContactBatch,
  mergeContactBatch,
} from './CommandBar/actionsBySection/contactBatchActions';
import { useHotkeyHandler } from '@hooks/use-hotkey-handler';
import ContactPersonModal from '@components/Modals/ContactPerson';
import { ModalType } from '@reducers/ui/modal';
import Contact from '@models/Contact';
import { useNavigate, useLocation } from 'react-router-dom';
import ShareContactModal from '@components/Modals/ShareContact';
import { SupportModal } from './Support';
import ShareGroupModal from '@components/Modals/ShareGroup';
import RequestMissingBirthdaysModal from '@components/Modals/RequestMissingBirthdays';
import { useAuthContext } from '@hooks/use-auth-context';
import { useContactLimitExceeded } from '@support/hooks/use-contact-limit-exceeded';
import { SettingsModal } from './Settings';
import SearchModal from './SearchModal';
import ExpandedActivityItemModal from '@components/Modals/ExpandedActivityItem';
import ShareInviteModal from '@components/Modals/ShareInvite';
import IAPUpgradeModal from '@components/Modals/IAPUpgrade';
import SearchEducationModal from '@components/Modals/SearchEducation';
import GroupReconnectModal from '@components/Modals/GroupReconnect';

interface ModalProps {
  closeModal: () => void;
  overlayClassName?: string;
  skipAutoFocus?: boolean;
  contact?: Contact;
}

type ModalComponent = (props: ModalProps) => JSX.Element;

const modalTypeToComponent: { [K in ModalType]: ModalComponent } = {
  expandedActivityItem: ExpandedActivityItemModal,
  commandBar: CommandBarModal,
  reconnect: ReconnectModal,
  merge: MergeModal,
  addToGroups: AddToGroupModal,
  contactAndNote: ContactAndNoteModal,
  contactPerson: ContactPersonModal,
  contactSearch: SearchModal,
  groupReconnect: GroupReconnectModal,
  iAPUpgrade: IAPUpgradeModal,
  searchEducation: SearchEducationModal,
  shareContact: ShareContactModal,
  shareGroup: ShareGroupModal,
  shareInvite: ShareInviteModal,
  support: SupportModal,
  requestMissingBirthdays: RequestMissingBirthdaysModal,
  settings: SettingsModal,
};

export default function ModalController() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { contactIsRestricted } = useAuthContext();
  const { isOpen, modalType, settingsNav } = useSelector(modalStateSelector);
  const selectedContact = useSelector(selectSelectedContact);
  const selectedContactBatch = useSelector(selectSelectedContactBatch);
  const isMultiselectView = useSelector(selectIsMultiselectView);
  const contactLimitExceeded = useContactLimitExceeded();
  const OpenedModal = modalTypeToComponent[modalType];
  const closeModal = () => dispatch(closeModalAction);

  useHotkeyHandler(
    {
      handler: (e) => {
        const hasContactBatch = isMultiselectView && selectedContactBatch.size > 1;

        if (isOpen || metaTrigger(e) || window.__activeConfirm) {
          return;
        }

        if (e.key == 'n') {
          e.preventDefault();
          dispatch(startNewNote);
          return;
        }

        if (e.key == 'r') {
          e.preventDefault();
          dispatch(startNewReminder);
          return;
        }

        if (e.key == 'p' && !contactLimitExceeded) {
          e.preventDefault();
          dispatch(startNewContact());
          return;
        }

        if (e.key == 'm' && (selectedContact || hasContactBatch)) {
          if (hasContactBatch) {
            mergeContactBatch(selectedContactBatch, 'Keyboard Shortcut', dispatch)();
          } else if (selectedContact) {
            dispatch(openModal('merge'));
          }

          e.preventDefault();
          return;
        }

        if (
          e.key == 'h' &&
          selectedContact &&
          !contactIsRestricted(selectedContact) &&
          !hasContactBatch
        ) {
          e.preventDefault();
          dispatch(openModal('reconnect'));
          return;
        }

        if (e.key == 'l' && (selectedContact || hasContactBatch)) {
          e.preventDefault();
          dispatch(openModal('addToGroups'));
          return;
        }

        if (
          e.key == 'c' &&
          selectedContact &&
          !contactIsRestricted(selectedContact) &&
          !hasContactBatch
        ) {
          e.preventDefault();
          dispatch(openModal('contactPerson'));
        }

        // This doesn't really fit with the current structure. Probably makes sense to refactor
        // the action handling into client.tsx or a separate component in the tree that just has
        // hotkeys/listeners but doesn't return anything
        if (e.key == 's' && selectedContact && !hasContactBatch) {
          e.preventDefault();
          toggleStarredStatus(selectedContact.id);
          return;
        }

        if (e.key == '#' && (selectedContact || hasContactBatch) && !e.defaultPrevented) {
          e.preventDefault();
          if (hasContactBatch) {
            archiveContactBatch(selectedContactBatch, 'Keyboard Shortcut', dispatch);
          } else if (selectedContact) {
            archiveContact(selectedContact, location, navigate, dispatch);
          }
          return;
        }
      },
    },
    [isOpen, selectedContact, selectedContactBatch],
  );

  useEffect(() => {
    const handleCommandBarOpening = (e) => {
      if (window.__activeConfirm) {
        return;
      }

      if (!isOpen) {
        if (e.keyCode === 75 && metaTrigger(e)) {
          e.preventDefault();
          dispatch(openModal('commandBar'));
          return;
        }
      }

      if (e.keyCode === 75 && metaTrigger(e)) {
        e.preventDefault();
        closeModal();
      }
    };

    window.app.on('keydown:all', handleCommandBarOpening);

    return () => window.app.off('keydown:all', handleCommandBarOpening);
  }, [isOpen]);

  return (
    <>
      {/* Settings can open children modals (e.g. "Request Missing Birthdays"). To simplfiy, we just treat them as separate nodes (settings and everything else). */}
      {/* Currently, contactAndNote modal is the only one that should be openable underneath settings (we should be able to treat Settings as a child of Focus Mode) */}
      {settingsNav && modalType !== 'contactAndNote' && (
        <SettingsModal closeModal={() => dispatch(closeSettings)} />
      )}
      {isOpen && OpenedModal && <OpenedModal closeModal={closeModal} />}
      {settingsNav && modalType === 'contactAndNote' && (
        <SettingsModal closeModal={() => dispatch(closeSettings)} />
      )}
    </>
  );
}
